import React from 'react';
import './DashboardSkeleton.css';

/**
 * 대시보드 스켈레톤 UI 컴포넌트
 * 실제 대시보드 템플릿의 구조를 기반으로 동적으로 스켈레톤을 생성합니다.
 */
const DashboardSkeleton = ({ 
  dashboardTemplate = null, 
  dashboardType = 'simple' 
}) => {
  
  // 기본 폴백 스켈레톤 (템플릿 로딩 실패 시)
  const fallbackSkeleton = [
    { id: 'skeleton-1', position: { x: 0, y: 0, w: 2, h: 2 }, title: '로딩 중', type: 'badge' },
    { id: 'skeleton-2', position: { x: 2, y: 0, w: 2, h: 2 }, title: '로딩 중', type: 'badge' },
    { id: 'skeleton-3', position: { x: 4, y: 0, w: 2, h: 2 }, title: '로딩 중', type: 'badge' },
    { id: 'skeleton-4', position: { x: 6, y: 0, w: 2, h: 2 }, title: '로딩 중', type: 'badge' },
    { id: 'skeleton-5', position: { x: 8, y: 0, w: 2, h: 2 }, title: '로딩 중', type: 'badge' },
    { id: 'skeleton-6', position: { x: 10, y: 0, w: 2, h: 2 }, title: '로딩 중', type: 'badge' },
  ];

  // 템플릿이 있으면 동적으로 스켈레톤 생성
  const generateSkeletonFromTemplate = (template) => {
    if (!template || !template.components || !Array.isArray(template.components)) {
      return fallbackSkeleton;
    }

    return template.components.map((component, index) => ({
      id: `skeleton-${component.id || index}`,
      position: component.position || { x: 0, y: 0, w: 2, h: 2 },
      title: component.title || '로딩 중...',
      type: component.type || 'badge',
      chartType: component.chart_options?.viz_type || component.type || 'badge'
    }));
  };

  const skeletonItems = dashboardTemplate 
    ? generateSkeletonFromTemplate(dashboardTemplate)
    : fallbackSkeleton;

  return (
    <div className="dashboard-skeleton">
      <div className="dashboard-skeleton-header">
        <div className="skeleton-title">
          <div className="skeleton-shimmer skeleton-text-large"></div>
        </div>
        <div className="skeleton-subtitle">
          <div className="skeleton-shimmer skeleton-text-small"></div>
        </div>
      </div>
      
      <div className="dashboard-skeleton-grid">
        {skeletonItems.map((item) => {
          // 차트 타입에 따른 스켈레톤 구조 결정
          const isBadgeType = item.chartType === 'badge' || item.type === 'badge';
          const isLargeChart = item.chartType === 'bar' || item.chartType === 'line' || item.chartType === 'pie';
          
          return (
            <div
              key={item.id}
              className={`dashboard-skeleton-component ${isBadgeType ? 'skeleton-badge' : 'skeleton-chart'} ${isLargeChart ? 'skeleton-large' : ''}`}
              style={{
                gridColumn: `${item.position.x + 1} / span ${item.position.w}`,
                gridRow: `${item.position.y + 1} / span ${item.position.h}`,
              }}
            >
              {isBadgeType ? (
                // 배지 타입 스켈레톤
                <div className="skeleton-badge-content">
                  <div className="skeleton-badge-title">
                    <div className="skeleton-shimmer skeleton-text-medium"></div>
                  </div>
                  <div className="skeleton-badge-value">
                    <div className="skeleton-shimmer skeleton-number-large"></div>
                  </div>
                  <div className="skeleton-badge-unit">
                    <div className="skeleton-shimmer skeleton-text-tiny"></div>
                  </div>
                </div>
              ) : (
                // 차트 타입 스켈레톤
                <div className="skeleton-chart-content">
                  <div className="skeleton-chart-header">
                    <div className="skeleton-shimmer skeleton-text-medium"></div>
                  </div>
                  <div className="skeleton-chart-body">
                    {item.chartType === 'line' || item.chartType === 'bar' ? (
                      <div className="skeleton-chart-graph">
                        <div className="skeleton-shimmer skeleton-graph-area"></div>
                      </div>
                    ) : item.chartType === 'pie' ? (
                      <div className="skeleton-chart-pie">
                        <div className="skeleton-shimmer skeleton-pie-circle"></div>
                      </div>
                    ) : item.chartType === 'gauge' ? (
                      <div className="skeleton-chart-gauge">
                        <div className="skeleton-shimmer skeleton-gauge-arc"></div>
                      </div>
                    ) : (
                      <div className="skeleton-chart-generic">
                        <div className="skeleton-shimmer skeleton-generic-area"></div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardSkeleton; 